import { POST_TYPE_FIELD_KEY } from '@citizen-walk/shared-types';
import { Button } from '@citizen-walk/shared-ui';
import { CollapsibleSection } from './CollapsibleSection';
import { ColorInput } from './ColorInput';
import type { CardPrintField } from './cardLayout';
import { DEFAULT_BADGE_FONT_PT, isOverlayOnlyField } from './builtinFields';
import { FIT_MODE_LABELS, resolveFieldDisplayLabel, type FitMode } from './fieldText';

/**
 * AW6 Critical #8 — 印刷ジョブ用の表示項目エディタ (右ペイン)。
 *
 * old2 `CardLayoutSettingsPanel.tsx` L42-89 を v2 に移植。地区担当者が **master template を
 * 変えずに** その印刷ジョブ限定で項目を 追加 / 削除 / 並べ替え できる。
 *
 * AW6 follow-up #2 (項目別書式): 各項目に「⚙ 書式」パネルを追加し、項目ごとに
 *   - 項目名 (データ設定の日本語名) を「項目名: 値」で表示するか (showLabel)
 *   - フォントサイズ / 太字 / 文字色
 *   - 最大文字数 (maxChars、超過は …)
 *   - 改行の有無 (wrap) / 最大行数 (maxLines)
 *   - テキスト量に応じた自動調整 (fit: 固定 / 自動縮小 / 行数可変)
 * を設定できる。QR として描画される項目 (qrCode) は書式対象外。
 *
 * カテゴリバッジ (category) は専用の書式パネル (CategoryBadgeStyleEditor) を持ち、
 * フォントサイズ・太字 (style.fontSize / style.bold) と「カテゴリごとの色」
 * (theme.categoryColors) を編集できる。カテゴリ色の編集 UI はこのタブに一本化されており
 * (テーマタブには出さない)、preview / PDF と同じ theme.categoryColors を単一ソースに編集する。
 * テーマ (プリセット) を切替えても、ここで指定した色は維持される (ThemeEditor.applyPreset で引き継ぐ)。
 */

export interface FieldsEditorProps {
  /** 現在のジョブ用 field 配列 (順序付き)。 */
  jobFields: CardPrintField[];
  /** 追加候補 (base template fields + 発見した extraFields)、jobFields 未含のもの。 */
  addableFields: CardPrintField[];
  onChange: (next: CardPrintField[]) => void;
  /** base template の fields に戻す。 */
  onReset: () => void;
  /** データ設定 postFields の key→日本語名 (項目名カスタム入力の placeholder 用)。 */
  labelMap: Record<string, string>;
  /**
   * カテゴリラベル → バッジ色 (theme.categoryColors)。バッジ書式パネルで編集する。
   * 未指定 / onCategoryColorsChange 未指定なら色編集 UI は出さない (フォント書式のみ)。
   */
  categoryColors?: Record<string, string>;
  /** 色未設定カテゴリの既定 (theme.accent)。色ピッカーの初期表示に使う。 */
  accentColor?: string;
  /** 投稿に実在する category ラベル群 (色編集の候補をプリポピュレートする)。 */
  categoryLabels?: string[];
  /** カテゴリ色マップ更新時のコールバック (theme.categoryColors を差し替える)。 */
  onCategoryColorsChange?: (next: Record<string, string>) => void;
  /**
   * カテゴリバッジの対象に選べる入力項目 (地区 postFields のうち選択式)。
   * 未指定 / onBadgeFieldChange 未指定なら対象項目セレクタは出さない。
   */
  badgeFieldCandidates?: { key: string; label: string }[];
  /** バッジ対象項目 (category フィールドの fieldPath) 変更時のコールバック。 */
  onBadgeFieldChange?: (fieldPath: string) => void;
  disabled?: boolean;
}

type FieldStyle = NonNullable<CardPrintField['style']>;

const FONT_SIZE_OPTIONS = [6, 7, 8, 9, 10, 11, 12, 14, 16];

export function FieldsEditor({
  jobFields,
  addableFields,
  onChange,
  onReset,
  labelMap,
  categoryColors,
  accentColor = '#2563eb',
  categoryLabels,
  onCategoryColorsChange,
  badgeFieldCandidates,
  onBadgeFieldChange,
  disabled,
}: FieldsEditorProps): JSX.Element {
  function removeField(key: string): void {
    onChange(jobFields.filter((f) => f.key !== key));
  }

  function moveField(index: number, dir: -1 | 1): void {
    const target = index + dir;
    if (target < 0 || target >= jobFields.length) return;
    const next = [...jobFields];
    [next[index], next[target]] = [next[target]!, next[index]!];
    onChange(next);
  }

  function addField(field: CardPrintField): void {
    if (jobFields.some((f) => f.key === field.key)) return;
    onChange([...jobFields, field]);
  }

  /** index の field の style を patch (undefined 値はキー削除、空 style は style ごと削除)。 */
  function patchStyle(index: number, patch: Partial<FieldStyle>): void {
    const cur = jobFields[index];
    if (!cur) return;
    const merged: Record<string, unknown> = { ...(cur.style ?? {}), ...patch };
    for (const k of Object.keys(merged)) {
      if (merged[k] === undefined) delete merged[k];
    }
    const next = [...jobFields];
    next[index] = {
      ...cur,
      style: Object.keys(merged).length > 0 ? (merged as FieldStyle) : undefined,
    };
    onChange(next);
  }

  return (
    <div className="grid grid-cols-1 gap-2 text-sm" data-testid="card-fields-editor">
      <CollapsibleSection title="表示項目 (順序付き)" defaultOpen>
        <div className="flex justify-end mb-1">
          <button
            type="button"
            onClick={onReset}
            disabled={disabled}
            className="text-[10px] text-slate-500 hover:text-slate-700 underline disabled:opacity-40"
            aria-label="表示項目をテンプレート既定に戻す"
          >
            テンプレ既定に戻す
          </button>
        </div>
        <div className="border border-slate-200 rounded">
          {jobFields.length === 0 && (
            <div className="p-2 text-xs text-slate-500">項目を追加してください</div>
          )}
          {jobFields.map((field, i) => (
            <div
              key={field.key}
              className="border-b border-slate-100 last:border-b-0"
              data-testid="card-field-row"
            >
              <div className="flex items-center gap-1 px-2 py-1">
                <span
                  className="flex-1 min-w-0 text-xs truncate"
                  title={`${field.label} (${field.source}.${field.fieldPath})`}
                >
                  {field.label}
                </span>
                <button
                  type="button"
                  onClick={() => moveField(i, -1)}
                  disabled={disabled || i === 0}
                  aria-label={`${field.label} を上へ`}
                  className="px-1.5 py-0.5 text-xs bg-slate-100 rounded disabled:opacity-30"
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => moveField(i, 1)}
                  disabled={disabled || i === jobFields.length - 1}
                  aria-label={`${field.label} を下へ`}
                  className="px-1.5 py-0.5 text-xs bg-slate-100 rounded disabled:opacity-30"
                >
                  ↓
                </button>
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  onClick={() => removeField(field.key)}
                  disabled={disabled}
                  aria-label={`${field.label} を削除`}
                >
                  ×
                </Button>
              </div>
              {field.key === 'qrCode' ? (
                <p className="px-2 pb-1 text-[10px] text-slate-400">
                  QR コードとして右下に描画されます。スキャンすると Google
                  マップで投稿位置を開きます (位置情報のある投稿のみ)。
                </p>
              ) : field.key === 'category' ? (
                <CategoryBadgeStyleEditor
                  field={field}
                  disabled={disabled}
                  onPatch={(patch) => patchStyle(i, patch)}
                  categoryColors={categoryColors}
                  accentColor={accentColor}
                  categoryLabels={categoryLabels}
                  onCategoryColorsChange={onCategoryColorsChange}
                  badgeFieldCandidates={badgeFieldCandidates}
                  onBadgeFieldChange={onBadgeFieldChange}
                />
              ) : isOverlayOnlyField(field) ? (
                <p className="px-2 pb-1 text-[10px] text-slate-400">
                  バッジ / オーバーレイとして描画されます (書式設定なし)。
                </p>
              ) : (
                <FieldStyleEditor
                  field={field}
                  labelMap={labelMap}
                  disabled={disabled}
                  onPatch={(patch) => patchStyle(i, patch)}
                />
              )}
            </div>
          ))}
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="追加できる項目" defaultOpen>
        {addableFields.length === 0 ? (
          <p className="text-[11px] text-slate-500">追加できる項目はありません。</p>
        ) : (
          <div className="grid grid-cols-2 gap-1">
            {addableFields.map((field) => (
              <button
                key={field.key}
                type="button"
                onClick={() => addField(field)}
                disabled={disabled}
                className="px-2 py-1 text-xs bg-slate-100 hover:bg-primary-50 rounded text-left disabled:opacity-40"
                aria-label={`${field.label} を追加`}
              >
                + {field.label}
              </button>
            ))}
          </div>
        )}
      </CollapsibleSection>
    </div>
  );
}

interface FieldStyleEditorProps {
  field: CardPrintField;
  labelMap: Record<string, string>;
  disabled?: boolean;
  onPatch: (patch: Partial<FieldStyle>) => void;
}

/** 1 項目の書式 (⚙) サブパネル。`<details>` で折りたたみ。 */
function FieldStyleEditor({
  field,
  labelMap,
  disabled,
  onPatch,
}: FieldStyleEditorProps): JSX.Element {
  const s = field.style ?? {};
  const wrapValue = s.wrap === false ? 'nowrap' : 'wrap';
  const fitValue: FitMode = s.fit ?? 'fixed';
  // 項目名カスタム入力の placeholder = 自動解決される既定ラベル (未入力時に使われる名前)。
  const autoLabel = resolveFieldDisplayLabel(field, labelMap);

  return (
    <details className="px-2 pb-2">
      <summary className="text-[10px] text-slate-500 hover:text-slate-700 cursor-pointer select-none">
        ⚙ 書式設定
      </summary>
      <div className="grid grid-cols-1 gap-2 mt-2 pl-1 text-[11px] text-slate-700">
        {/* 項目名 (日本語) の表示 */}
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={s.showLabel === true}
            disabled={disabled}
            onChange={(e) =>
              // OFF 時はカスタム項目名 (labelText) も一緒にクリア (死にデータを残さない)。
              onPatch(
                e.target.checked
                  ? { showLabel: true }
                  : { showLabel: undefined, labelText: undefined },
              )
            }
            aria-label={`${field.label} に項目名を表示`}
          />
          項目名を表示 (「項目名: 値」)
        </label>

        {/* showLabel ON のときだけ、項目名を任意の名称に上書きできる入力欄を出す */}
        {s.showLabel === true && (
          <div className="grid grid-cols-1 gap-0.5 pl-5">
            <input
              type="text"
              value={s.labelText ?? ''}
              disabled={disabled}
              placeholder={autoLabel || '項目名'}
              onChange={(e) => onPatch({ labelText: e.target.value ? e.target.value : undefined })}
              aria-label={`${field.label} の項目名 (カスタム)`}
              className="border border-slate-300 rounded px-1 py-0.5 bg-white"
            />
            <span className="text-[10px] text-slate-400">
              空欄なら自動名「{autoLabel || '(なし)'}」を使用
            </span>
          </div>
        )}

        {/* AI 分類コード → 日本語名 (M2 カテゴリマスタ駆動) */}
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={s.resolveCodes === true}
            disabled={disabled}
            onChange={(e) => onPatch({ resolveCodes: e.target.checked ? true : undefined })}
            aria-label={`${field.label} のコードを日本語名に変換`}
          />
          コードを日本語名に変換 (AI分類など)
        </label>

        {/* フォントサイズ / 太字 */}
        <div className="flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-1">
            サイズ
            <select
              value={s.fontSize ?? ''}
              disabled={disabled}
              onChange={(e) =>
                onPatch({ fontSize: e.target.value ? Number(e.target.value) : undefined })
              }
              aria-label={`${field.label} のフォントサイズ`}
              className="border border-slate-300 rounded px-1 py-0.5 bg-white"
            >
              <option value="">自動</option>
              {FONT_SIZE_OPTIONS.map((pt) => (
                <option key={pt} value={pt}>
                  {pt}pt
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1">
            <input
              type="checkbox"
              checked={s.bold === true}
              disabled={disabled}
              onChange={(e) => onPatch({ bold: e.target.checked ? true : undefined })}
              aria-label={`${field.label} を太字`}
            />
            太字
          </label>
          <label className="flex items-center gap-1">
            色
            <ColorInput
              value={s.color ?? '#0f172a'}
              disabled={disabled}
              onChange={(c) => onPatch({ color: c })}
              ariaLabel={`${field.label} の文字色`}
              size="sm"
            />
            {s.color ? (
              <button
                type="button"
                onClick={() => onPatch({ color: undefined })}
                disabled={disabled}
                className="text-[10px] text-slate-500 underline"
                aria-label={`${field.label} の文字色をクリア`}
              >
                クリア
              </button>
            ) : (
              <span className="text-[10px] text-slate-400">テーマ既定</span>
            )}
          </label>
        </div>

        {/* 最大文字数 */}
        <label className="flex items-center gap-1">
          最大文字数
          <input
            type="number"
            min={0}
            value={s.maxChars ?? ''}
            disabled={disabled}
            placeholder="無制限"
            onChange={(e) =>
              onPatch({
                maxChars: e.target.value ? Math.max(0, Number(e.target.value)) : undefined,
              })
            }
            aria-label={`${field.label} の最大文字数`}
            className="w-16 border border-slate-300 rounded px-1 py-0.5"
          />
          <span className="text-slate-400">超過は … で省略</span>
        </label>

        {/* 改行 / 最大行数 */}
        <div className="flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-1">
            改行
            <select
              value={wrapValue}
              disabled={disabled}
              onChange={(e) =>
                // 折り返さない時は maxLines もクリア (隠れた死にデータを残さない)。
                e.target.value === 'nowrap'
                  ? onPatch({ wrap: false, maxLines: undefined })
                  : onPatch({ wrap: undefined })
              }
              aria-label={`${field.label} の改行`}
              className="border border-slate-300 rounded px-1 py-0.5 bg-white"
            >
              <option value="wrap">折り返す</option>
              <option value="nowrap">折り返さない (1 行)</option>
            </select>
          </label>
          {wrapValue === 'wrap' && (
            <label className="flex items-center gap-1">
              最大行数
              <input
                type="number"
                min={1}
                max={8}
                value={s.maxLines ?? ''}
                disabled={disabled}
                placeholder="2"
                onChange={(e) =>
                  onPatch({
                    maxLines: e.target.value
                      ? Math.min(8, Math.max(1, Number(e.target.value)))
                      : undefined,
                  })
                }
                aria-label={`${field.label} の最大行数`}
                className="w-14 border border-slate-300 rounded px-1 py-0.5"
              />
            </label>
          )}
        </div>

        {/* 自動調整 (fit) */}
        <label className="flex items-center gap-1">
          自動調整
          <select
            value={fitValue}
            disabled={disabled}
            onChange={(e) =>
              onPatch({ fit: e.target.value === 'fixed' ? undefined : (e.target.value as FitMode) })
            }
            aria-label={`${field.label} の自動調整`}
            className="border border-slate-300 rounded px-1 py-0.5 bg-white flex-1 min-w-0"
          >
            <option value="fixed">{FIT_MODE_LABELS.fixed}</option>
            <option value="shrink">{FIT_MODE_LABELS.shrink}</option>
            <option value="grow">{FIT_MODE_LABELS.grow}</option>
          </select>
        </label>
        {fitValue === 'shrink' && (
          <p className="text-[10px] text-slate-400 leading-tight">
            ※
            プレビューは推定フォント、印刷(PDF)は実フォント幅で再計算するため、最終サイズが多少異なります。
          </p>
        )}
      </div>
    </details>
  );
}

interface CategoryBadgeStyleEditorProps {
  field: CardPrintField;
  disabled?: boolean;
  /** フォントサイズ・太字 (style.fontSize / style.bold) の patch。 */
  onPatch: (patch: Partial<FieldStyle>) => void;
  /** カテゴリラベル → バッジ色 (theme.categoryColors)。 */
  categoryColors?: Record<string, string>;
  /** 色未設定カテゴリの既定 (theme.accent)。 */
  accentColor: string;
  /** 投稿に実在する category ラベル群 (色編集候補のプリポピュレート)。 */
  categoryLabels?: string[];
  /** カテゴリ色マップ更新時のコールバック (未指定なら色編集 UI を出さない)。 */
  onCategoryColorsChange?: (next: Record<string, string>) => void;
  /** バッジ対象に選べる入力項目 (選択式 postFields)。 */
  badgeFieldCandidates?: { key: string; label: string }[];
  /** バッジ対象項目 (fieldPath) 変更時のコールバック (未指定なら対象セレクタを出さない)。 */
  onBadgeFieldChange?: (fieldPath: string) => void;
}

/**
 * カテゴリバッジ専用の書式 (⚙) パネル。
 *
 * バッジは overlay 専用 (テキスト行に流れない) ため、流し込みテキスト向けの設定
 * (最大文字数・改行・自動調整・項目名表示) は出さず、バッジに効く設定だけを出す:
 *   - フォントサイズ (style.fontSize、未設定は 7pt)
 *   - 太字 (style.bold、未設定は太字)
 *   - カテゴリごとの色 (theme.categoryColors、投稿に実在するラベルでプリポピュレート)
 *
 * 色は preview / PDF と同じ theme.categoryColors を編集する (カテゴリ色の編集はこのパネルに一本化)。
 * 未設定ラベルはメイン色 (accent) のバッジになる。
 */
function CategoryBadgeStyleEditor({
  field,
  disabled,
  onPatch,
  categoryColors,
  accentColor,
  categoryLabels,
  onCategoryColorsChange,
  badgeFieldCandidates,
  onBadgeFieldChange,
}: CategoryBadgeStyleEditorProps): JSX.Element {
  const s = field.style ?? {};
  const colorMap = categoryColors ?? {};
  // 編集候補 = 投稿に実在するラベル ∪ 既に色を割り当て済みのラベル (昇順)。
  const labels = [...new Set([...(categoryLabels ?? []), ...Object.keys(colorMap)])].sort();
  const showColorEditor = !!onCategoryColorsChange;

  // 対象項目セレクタ: 現在の対象 (fieldPath、既定 投稿タイプ) が候補に無ければ先頭に補う。
  const currentPath = field.fieldPath || POST_TYPE_FIELD_KEY;
  const candidates = badgeFieldCandidates ?? [];
  const showFieldSelector = !!onBadgeFieldChange;
  const selectorOptions = candidates.some((c) => c.key === currentPath)
    ? candidates
    : [{ key: currentPath, label: currentPath }, ...candidates];

  function setCategoryColor(label: string, color: string): void {
    onCategoryColorsChange?.({ ...colorMap, [label]: color });
  }
  function clearCategoryColor(label: string): void {
    if (!onCategoryColorsChange) return;
    const next = { ...colorMap };
    delete next[label];
    onCategoryColorsChange(next);
  }

  return (
    <details className="px-2 pb-2">
      <summary className="text-[10px] text-slate-500 hover:text-slate-700 cursor-pointer select-none">
        ⚙ 書式設定 (カテゴリバッジ)
      </summary>
      <div className="grid grid-cols-1 gap-2 mt-2 pl-1 text-[11px] text-slate-700">
        <p className="text-[10px] text-slate-400 leading-tight">
          カテゴリバッジはカード左上に表示されます。対象項目・フォントサイズ・太字とカテゴリごとの色を設定できます。
        </p>

        {/* バッジの対象項目 (選択式 postField) */}
        {showFieldSelector && (
          <div className="grid grid-cols-1 gap-0.5">
            <label className="flex items-center gap-1">
              対象項目
              <select
                value={currentPath}
                disabled={disabled}
                onChange={(e) => onBadgeFieldChange?.(e.target.value)}
                aria-label="カテゴリバッジの対象項目"
                className="border border-slate-300 rounded px-1 py-0.5 bg-white flex-1 min-w-0"
              >
                {selectorOptions.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
            {candidates.length === 0 ? (
              <span className="text-[10px] text-slate-400">
                選択式の入力項目がありません。データ設定で選択式 (select/複数選択)
                の項目を作るとここで選べます。
              </span>
            ) : (
              <span className="text-[10px] text-slate-400">
                バッジに表示する投稿の入力項目です (既定: カテゴリ)。
              </span>
            )}
          </div>
        )}

        {/* フォントサイズ / 太字 */}
        <div className="flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-1">
            サイズ
            <select
              value={s.fontSize ?? ''}
              disabled={disabled}
              onChange={(e) =>
                onPatch({ fontSize: e.target.value ? Number(e.target.value) : undefined })
              }
              aria-label="カテゴリバッジのフォントサイズ"
              className="border border-slate-300 rounded px-1 py-0.5 bg-white"
            >
              <option value="">既定 ({DEFAULT_BADGE_FONT_PT}pt)</option>
              {FONT_SIZE_OPTIONS.map((pt) => (
                <option key={pt} value={pt}>
                  {pt}pt
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1">
            <input
              type="checkbox"
              checked={s.bold !== false}
              disabled={disabled}
              // 既定は太字。チェックを外したときだけ bold:false を残し、戻したら既定 (undefined) に。
              onChange={(e) => onPatch({ bold: e.target.checked ? undefined : false })}
              aria-label="カテゴリバッジを太字"
            />
            太字
          </label>
        </div>

        {/* カテゴリごとの色 */}
        {showColorEditor && (
          <div className="grid grid-cols-1 gap-1">
            <span className="text-[10px] font-semibold text-slate-500">カテゴリごとの色</span>
            {labels.length === 0 ? (
              <p className="text-[10px] text-slate-400 leading-tight">
                カテゴリを持つ投稿がまだありません。投稿を読み込むとここに一覧が表示されます。
              </p>
            ) : (
              labels.map((label) => {
                const explicit = Object.prototype.hasOwnProperty.call(colorMap, label);
                const color = explicit ? colorMap[label]! : accentColor;
                return (
                  <div
                    key={label}
                    className="grid grid-cols-1 gap-0.5 border-b border-slate-100 pb-1 last:border-b-0"
                    data-testid="badge-category-color-row"
                  >
                    <div className="flex items-center gap-1">
                      <span className="flex-1 truncate text-slate-700" title={label}>
                        {label}
                      </span>
                      {explicit ? (
                        <button
                          type="button"
                          onClick={() => clearCategoryColor(label)}
                          disabled={disabled}
                          className="text-[10px] text-slate-500 underline"
                          aria-label={`カテゴリ ${label} の色を既定に戻す`}
                        >
                          既定に戻す
                        </button>
                      ) : (
                        <span className="text-[10px] text-slate-400">既定</span>
                      )}
                    </div>
                    <ColorInput
                      value={color}
                      disabled={disabled}
                      onChange={(v) => setCategoryColor(label, v)}
                      ariaLabel={`カテゴリ ${label} の色`}
                      size="sm"
                    />
                  </div>
                );
              })
            )}
            <p className="text-[10px] text-slate-400 leading-tight">
              「既定」はメイン色のバッジになります。ここで指定した色はテーマ (プリセット)
              を切替えても維持されます。
            </p>
          </div>
        )}
      </div>
    </details>
  );
}
